/**
 * FreshnessIndicator — compact "Data: fresh ●" indicator for the Shell top bar.
 *
 * Polls GET /api/freshness every 60 s.
 * Derives an overall status from the worst individual source.
 */
import { useState, useEffect } from 'react'
import { api } from '@/lib/api'

interface FreshnessSource {
  last_updated: string | null
  age_seconds: number | null
  status: 'fresh' | 'aging' | 'stale' | 'unavailable'
}

type FreshnessResponse = Record<string, FreshnessSource>

const STATUS_COLOR: Record<string, string> = {
  fresh:       '#10b981',
  aging:       '#f59e0b',
  stale:       '#f43f5e',
  unavailable: '#64748b',
}

const STATUS_ORDER = ['stale', 'unavailable', 'aging', 'fresh']

function worstStatus(data: FreshnessResponse): string {
  const statuses = Object.values(data).map(s => s.status)
  for (const s of STATUS_ORDER) {
    if (statuses.includes(s as any)) return s
  }
  return 'unavailable'
}

export default function FreshnessIndicator() {
  const [status, setStatus] = useState<string | null>(null)
  const [tooltip, setTooltip] = useState<string>('')

  async function fetchFreshness() {
    try {
      const data: FreshnessResponse = await api.get('/api/freshness').then(r => r.data)
      const worst = worstStatus(data)
      setStatus(worst)

      // Build tooltip listing stale/aging sources
      const degraded = Object.entries(data)
        .filter(([, v]) => v.status !== 'fresh')
        .map(([k, v]) => `${k}: ${v.status}`)
      setTooltip(degraded.length > 0 ? degraded.join(' · ') : 'All sources fresh')
    } catch {
      setStatus('unavailable')
      setTooltip('Could not check data freshness')
    }
  }

  useEffect(() => {
    fetchFreshness()
    const id = setInterval(fetchFreshness, 60_000)
    return () => clearInterval(id)
  }, []) // eslint-disable-line

  if (!status) return null

  const color = STATUS_COLOR[status] ?? '#64748b'
  const label = status === 'fresh' ? 'fresh' : status === 'aging' ? 'aging' : status === 'stale' ? 'stale' : 'n/a'

  return (
    <div
      className="hidden lg:flex items-center gap-1.5 text-xs cursor-default"
      title={`Data freshness: ${tooltip}`}
      aria-label={`Data freshness: ${label}`}
    >
      <span className="text-text-muted">Data:</span>
      <span className="font-medium" style={{ color }}>
        {label}
      </span>
      <span
        className="inline-block w-2 h-2 rounded-full"
        style={{ backgroundColor: color }}
        aria-hidden
      />
    </div>
  )
}
